import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, Building2, UserCog, FileText, Calculator, Shield, Briefcase, Heart, Users, TrendingUp, Handshake, BookOpen, AlertCircle, Clock, Ban, RefreshCcw } from 'lucide-react';
import { SEO } from '@/components/SEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cxo-placement/artifacts/o33cdnbv_profile_pic.png";
const SHERATNA_LOGO = "https://customer-assets.emergentagent.com/job_cxo-placement/artifacts/ypt56t3t_ChatGPT%20Image%20Mar%208%2C%202026%2C%2011_17_26%20AM.png";

const services = [
  { icon: Calculator, title: 'Accounting', desc: 'Full-service bookkeeping & financial management' },
  { icon: FileText, title: 'Tax & Compliance', desc: 'GST, Income Tax, TDS & regulatory filings' },
  { icon: Building2, title: 'Registrations', desc: 'Company, LLP, Trust & NGO registrations' },
  { icon: Shield, title: 'Micro CFO', desc: 'For small businesses that need clarity, control, and decision support' },
  { icon: Briefcase, title: 'CXO Hiring', desc: 'Executive search for CFO, Tax & Legal roles' },
  { icon: UserCog, title: 'Advisory', desc: 'Business planning & growth strategy' },
];

const threeJewels = [
  { title: 'Right Vision', desc: 'Clear understanding of your business goals and financial landscape' },
  { title: 'Right Knowledge', desc: 'Expert insights backed by MBA and CA expertise with industry experience' },
  { title: 'Right Action', desc: 'Precise execution ensuring compliance and sustainable growth' },
];

const clientTypes = [
  { icon: Building2, title: 'Manufacturers', desc: 'Production units, factories, assembly operations' },
  { icon: UserCog, title: 'Service Providers', desc: 'Consultants, freelancers, professional services' },
  { icon: Briefcase, title: 'Traders', desc: 'Wholesale, retail, export/import businesses' },
  { icon: Heart, title: 'Non-Profits', desc: 'NGOs, trusts, Section 8 companies, foundations' },
];

const painPoints = [
  { icon: AlertCircle, text: 'Dealing with messy books and unclear financials' },
  { icon: Clock, text: 'Missing tax filings and compliance deadlines' },
  { icon: Ban, text: 'No in-house finance head or CFO guidance' },
  { icon: FileText, text: 'Struggling with registrations and paperwork' },
  { icon: TrendingUp, text: 'Missing out on government schemes and bank funding' },
  { icon: Shield, text: 'No clarity on cash flow or financial position despite running the business' },
];

export default function HomePage() {
  return (
    <div className="pt-20" data-testid="home-page">
      <SEO 
        path="/"
        description="Expert accounting, tax compliance, Micro CFO services for small businesses under ₹10 Crore and NGOs. MSME registration, government schemes guidance, and CXO hiring. Based in Hyderabad & Mumbai, serving Pan-India."
        keywords="accounting services, tax compliance, Micro CFO, MSME registration, micro business consulting, Hyderabad, Mumbai"
      />
      {/* CCFS-2026 Announcement Banner */}
      <div className="bg-[#E84C3D]" data-testid="ccfs-announcement-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-2 sm:py-2.5 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-4">
          <p className="text-white text-center sm:text-left text-xs sm:text-sm leading-snug">
            <span className="hidden sm:inline">Still holding an inactive company? Business closed already or balance sheet filing pending? Save ₹50,000 to ₹20,00,000+ in penalties under CCFS-2026.</span>
            <span className="sm:hidden">Inactive company? <strong>Save ₹8L+</strong> under CCFS-2026</span>
          </p>
          <Link to="/ccfs-2026-company-status-check" className="shrink-0">
            <Button size="sm" className="bg-white text-[#E84C3D] hover:bg-white/90 text-xs h-6 sm:h-7 px-2.5 sm:px-3 font-medium" data-testid="ccfs-banner-btn">
              Check Your Options <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
        <div className="text-center pb-1">
          <p className="text-[9px] sm:text-[10px] text-white/70">⏳ Relief window open until 15 July 2026 — MCA, Government of India scheme</p>
        </div>
      </div>

      {/* 1 Hero Section */}
      <section className="hero-gradient py-20 md:py-28" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fadeInUp">
              <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">
                Helping Micro Businesses Become Growth-Ready and Bank-Funding-Ready
              </span>
              <h1 className="font-secondary text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
                Right Vision.<br />
                Right Knowledge.<br />
                <span className="text-[#E84C3D]">Right Action.</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-4 max-w-xl">
                Simple, affordable support for businesses under ₹10 Crore revenue — from expert MBAs and Chartered Accountants.
              </p>
              <p className="text-base text-slate-600 mb-6 max-w-xl">
                From registrations and accounting to compliance, Micro CFO support, funding readiness, and hiring — we help you build a strong foundation for growth.
              </p>
              
              {/* Pain Points */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {painPoints.map((pain, i) => {
                  const Icon = pain.icon;
                  return (
                    <div key={i} className="flex items-center gap-2 text-slate-600">
                      <Icon className="w-4 h-4 text-[#E84C3D] shrink-0" />
                      <span className="text-sm">{pain.text}</span>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-4 mb-4">
                <Link to="/contact">
                  <Button className="btn-primary text-base px-6 py-6" data-testid="hero-cta-btn">
                    Schedule a Free Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="btn-secondary text-base px-6 py-6" data-testid="hero-services-btn">
                    Explore Services
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-slate-500">
                Best suited for businesses up to ₹10 Crore revenue and non-profits.
              </p>
            </div>
            <div className="lg:col-span-5 animate-fadeInUp animation-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#E84C3D]/5 rounded-3xl transform rotate-3"></div>
                <div className="relative bg-white p-8 rounded-2xl shadow-xl border border-slate-100">
                  <img src={LOGO_URL} alt="Ratnatrraya Consulting LLP" className="w-24 h-24 mx-auto mb-6" />
                  <h3 className="font-secondary text-xl text-center text-slate-900 mb-6">Why Choose Us?</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#E84C3D]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-[#E84C3D]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">MBA + CA Expertise</p>
                        <p className="text-xs text-slate-500">Strategic thinking meets technical excellence</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#059669]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-[#059669]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Built for Small Business</p>
                        <p className="text-xs text-slate-500">Pricing that fits your budget</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#E84C3D]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-[#E84C3D]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">End-to-End Support</p>
                        <p className="text-xs text-slate-500">From registration to growth strategy</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#059669]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Heart className="w-4 h-4 text-[#059669]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Women Entrepreneurs</p>
                        <p className="text-xs text-slate-500">Support to turn skills into income</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#E84C3D]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <TrendingUp className="w-4 h-4 text-[#E84C3D]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">Schemes & Funding</p>
                        <p className="text-xs text-slate-500">Guidance to access MSME schemes and bank loans</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2️⃣ Who We Serve */}
      <section className="py-20 md:py-28 bg-white" data-testid="who-we-serve-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">Who We Serve</span>
            <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Built for Growing Businesses
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We specialize in <strong className="text-slate-900">micro businesses under ₹10 Crore revenue</strong> and <strong className="text-slate-900">non-profit organizations</strong> — often overlooked by larger professional firms. We guide informal and early-stage businesses to become compliant, GST-registered, and eligible for MSME benefits and bank funding.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {clientTypes.map((client, index) => {
              const Icon = client.icon;
              return (
                <div 
                  key={index}
                  className="bg-[#F9F9F7] p-6 rounded-xl text-center hover:shadow-lg transition-shadow"
                  data-testid={`client-type-${index}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#E84C3D]/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-[#E84C3D]" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-1">{client.title}</h3>
                  <p className="text-slate-600 text-sm">{client.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-3 bg-[#059669]/10 px-5 py-2.5 rounded-full">
              <Check className="w-5 h-5 text-[#059669]" />
              <span className="text-[#059669] font-medium text-sm">Simple pricing • No hidden costs • Expert guidance</span>
            </div>
          </div>
        </div>
      </section>

      {/* 3️⃣ Key Metrics / Track Record */}
      <section className="py-16 md:py-20 bg-slate-900 text-white" data-testid="metrics-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-center mb-10">
            Our Track Record and Advisory Depth
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#E84C3D]/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#E84C3D]" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-[#E84C3D]">100+</div>
              <div className="text-sm text-slate-400 mt-1">Businesses supported across India</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#E84C3D]/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#E84C3D]" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-[#E84C3D]">50+</div>
              <div className="text-sm text-slate-400 mt-1">Types of registrations & compliances handled</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#E84C3D]/20 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#E84C3D]" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-[#E84C3D]">20+</div>
              <div className="text-sm text-slate-400 mt-1">CXO & senior leadership placements</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#E84C3D]/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#E84C3D]" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-[#E84C3D]">30+</div>
              <div className="text-sm text-slate-400 mt-1">Years of combined advisory experience</div>
            </div>
            <div className="text-center col-span-2 md:col-span-1">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#E84C3D]/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#E84C3D]" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-[#E84C3D]">40+</div>
              <div className="text-sm text-slate-400 mt-1">Professionals in our extended advisory network</div>
            </div>
          </div>
        </div>
      </section>

      {/* 4️⃣ What We Offer / Services */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">What We Offer</span>
              <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-2">
                Comprehensive Services
              </h2>
              <p className="text-slate-600 text-base max-w-xl">
                Everything your business needs under one roof — from basic bookkeeping to strategic CFO support.
              </p>
              <p className="text-slate-500 text-sm max-w-xl mt-2 italic">
                A Micro CFO layer for businesses that are too small for a full-time CFO, but too important to run without one.
              </p>
            </div>
            <Link to="/services">
              <Button variant="outline" className="btn-secondary">
                View All Services <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index}
                  className="service-card bg-[#F9F9F7] p-6 rounded-xl hover:shadow-lg transition-shadow"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#E84C3D]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#E84C3D]" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-600 text-sm">{service.desc}</p>
                </div>
              );
            })}
            {/* Company Reset Card */}
            <div 
              className="service-card bg-[#FEF2F2] border border-red-100 p-6 rounded-xl hover:shadow-lg transition-shadow"
              data-testid="service-card-company-reset"
            >
              <div className="w-12 h-12 rounded-xl bg-[#E84C3D]/10 flex items-center justify-center mb-4">
                <RefreshCcw className="w-6 h-6 text-[#E84C3D]" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Company Reset</h3>
              <p className="text-slate-600 text-sm mb-3">Close, pause or restart inactive companies with confidence. Use our 3-minute Company Reset Tool to evaluate your situation under CCFS-2026.</p>
              <Link to="/ccfs-2026-company-status-check">
                <Button size="sm" className="bg-[#E84C3D] hover:bg-[#C0392B] text-xs" data-testid="service-company-reset-btn">
                  Start Company Reset Check <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 5️⃣ Empowering Women Entrepreneurs (SheRatna) */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]" data-testid="sheratna-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="text-center mb-10">
            <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Empowering Women Entrepreneurs
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Through the <strong className="text-[#E84C3D]">SriRatna</strong> community, Ratnatrraya celebrates and supports women who turn skills, ideas, and effort into sustainable enterprises.
            </p>
          </div>

          {/* Two Card Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Card - Vision */}
            <div className="bg-white p-8 md:p-10 rounded-xl shadow-lg">
              <div className="mb-5">
                <img 
                  src="https://customer-assets.emergentagent.com/job_0d19990c-8310-445d-b78c-28da6111a2d6/artifacts/nomafqr2_Logo%20Sharp.png" 
                  alt="SriRatna" 
                  className="h-20 w-auto"
                />
              </div>
              <h3 className="font-semibold text-2xl text-slate-900 mb-2">
                SriRatna Community
              </h3>
              <p className="text-[#E84C3D] font-medium text-lg mb-4">
                Turning Skills into Sustainable Enterprises.
              </p>
              
              {/* Who is it for */}
              <div className="bg-[#F9F9F7] p-4 rounded-lg mb-4">
                <p className="text-sm font-medium text-slate-700">
                  <strong>Who is it for?</strong> Women with home-based businesses, early-stage ventures, or professional skills they want to turn into income and enterprise.
                </p>
              </div>

              <div className="space-y-3 text-slate-600 text-sm">
                <p>
                  Many women have powerful skills and ideas but lack access to guidance, networks, and markets.
                </p>
                <p>
                  <strong className="text-[#E84C3D]">SriRatna</strong> is a supportive community where women entrepreneurs learn, connect, and build enterprises with confidence.
                </p>
                <p className="italic text-slate-500">
                  In Indian tradition, &ldquo;Sri&rdquo; represents prosperity and auspiciousness. Every woman building an enterprise is a <strong>SriRatna</strong> — a gem creating prosperity for families and communities.
                </p>
              </div>
            </div>

            {/* Right Card - Programs + CTA */}
            <div className="bg-[#E53935] p-8 md:p-10 rounded-xl shadow-lg text-white">
              <h3 className="font-semibold text-2xl mb-5">
                How SriRatna Supports Women Entrepreneurs
              </h3>
              <ul className="space-y-3 mb-6 text-sm">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                    <BookOpen className="w-3 h-3 text-white" />
                  </div>
                  <span>Practical business and financial guidance</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Handshake className="w-3 h-3 text-white" />
                  </div>
                  <span>Mentorship from experienced professionals</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                    <FileText className="w-3 h-3 text-white" />
                  </div>
                  <span>Support with compliance and formalisation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Users className="w-3 h-3 text-white" />
                  </div>
                  <span>Community learning and collaboration</span>
                </li>
              </ul>
              <p className="text-white/90 mb-6 italic text-sm">
                Together we help women build confidence, livelihoods, and sustainable enterprises that grow over time.
              </p>
              <a 
                href="https://chat.whatsapp.com/FzI1na22cqM4e6SShoxxft?mode=gi_t" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-white text-[#E53935] hover:bg-white/90 font-semibold px-6 py-5 text-sm w-full md:w-auto"
                  data-testid="sheratna-cta-btn"
                >
                  <Users className="mr-2 h-4 w-4" />
                  Join the SriRatna Community
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 6️⃣ Philosophy (Ratnatrraya) */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]" data-testid="three-jewels-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">Our Philosophy</span>
            <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              The Three Jewels
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Inspired by the Jain principle of Ratnatrraya — the foundational path to excellence through vision, knowledge, and action.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {threeJewels.map((jewel, index) => (
              <div 
                key={index}
                className="relative p-6 bg-white rounded-xl group hover:shadow-xl transition-all duration-300"
                data-testid={`jewel-card-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-[#F9F9F7] shadow-lg flex items-center justify-center mb-5 group-hover:bg-[#E84C3D] group-hover:text-white transition-colors">
                  <span className="font-secondary text-xl font-bold">{index + 1}</span>
                </div>
                <h3 className="font-secondary text-xl font-semibold text-slate-900 mb-3">{jewel.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{jewel.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inactive Company Problem-Solution Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="inactive-company-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">CCFS-2026</span>
              <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                Do you have an inactive company?
              </h2>
              <p className="text-slate-600 mb-4">
                Many founders have companies that stopped operating but still exist legally. These companies can attract compliance penalties if ignored.
              </p>
              <p className="text-slate-700 font-medium mb-3">Our Company Reset solution helps founders:</p>
              <ul className="space-y-3 text-slate-600 mb-8">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
                  Close inactive companies
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
                  Apply for Dormant status
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
                  Regularise compliance and restart operations
                </li>
              </ul>
              <Link to="/ccfs-2026-company-status-check">
                <Button className="bg-[#E84C3D] hover:bg-[#C0392B] text-base px-6 py-5" data-testid="inactive-company-cta-btn">
                  Evaluate Your Company <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="relative bg-[#F9F9F7] rounded-2xl p-8 max-w-sm w-full">
                <div className="space-y-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                      <AlertCircle className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Pending ROC filings</p>
                      <p className="text-xs text-slate-500">Late fees accumulating each year</p>
                    </div>
                  </div>
                  <div className="border-l-2 border-dashed border-slate-300 h-4 ml-6"></div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                      <RefreshCcw className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Company Reset assessment</p>
                      <p className="text-xs text-slate-500">3-minute guided evaluation</p>
                    </div>
                  </div>
                  <div className="border-l-2 border-dashed border-slate-300 h-4 ml-6"></div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Clear path forward</p>
                      <p className="text-xs text-slate-500">Close, pause or restart with confidence</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7 Consultation CTA */}
      <section className="py-20 md:py-28 bg-slate-900 text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-secondary text-3xl md:text-4xl font-semibold mb-4">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Let's discuss how Ratnatrraya can help your micro business or non-profit achieve sustainable growth with the right vision, knowledge, and action.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <Link to="/contact">
              <Button className="btn-primary text-base px-6 py-5" data-testid="cta-contact-btn">
                Schedule a Free Consultation
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 text-base px-6 py-5">
                Learn About Us
              </Button>
            </Link>
          </div>
          <p className="text-sm text-slate-500">
            No obligation · Suited for businesses up to ₹10 Cr revenue.
          </p>
        </div>
      </section>
    </div>
  );
}
